import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ImagePlus, Upload } from 'lucide-react';

import CommonDropzone from '../../../components/common-dropzone/CommonDropzone';
import AssetsState from '../../components/assets-state/AssetsState';
import PanelBody from '../../components/panel-body/PanelBody';
import PanelSection from '../../components/panel-section/PanelSection';
import { useMediaStudio } from '../../context/MediaStudioContext';

import scss from './UploadPanel.module.scss';

function UploadPanel() {
  const { t } = useTranslation('mediaStudio');
  const { features, files } = useMediaStudio();
  const { images, isImagesLoading } = useSelector((state) => state.mediaStudioSettings);
  const inputRef = useRef(null);

  const addSource = (source, options) => {
    if (features.addLayer) {
      files.addLayer(source, options);
    } else {
      files.openImage(source);
    }
  };

  const handleFiles = (droppedFiles) => {
    const file = droppedFiles?.[0];
    if (file) {
      addSource(file);
    }
  };

  return (
    <PanelBody>
      <CommonDropzone className={scss.dropzone} onDrop={handleFiles}>
        {() => (
          <div className={scss.content}>
            <Upload size={22} strokeWidth={1.5} />
            <p className={scss.hint}>{features.addLayer ? t('uploadAddHint') : t('uploadOpenHint')}</p>
          </div>
        )}
      </CommonDropzone>

      <button type='button' className={scss.button} onClick={() => inputRef.current?.click()}>
        <ImagePlus size={16} />
        {features.addLayer ? t('addLayer') : t('openImage')}
      </button>
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        hidden
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />

      {features.brandImages ? (
        <PanelSection title={t('brandMedia')}>
          <AssetsState
            isLoading={isImagesLoading}
            isEmpty={!images.length}
            emptyText={t('brandMediaEmpty')}
            loaderWidth={28}
            className={scss.brandMediaState}>
            <div className={scss.brandMediaGrid}>
              {images.map((image) => (
                <button
                  key={image.id}
                  type='button'
                  className={scss.brandMediaItem}
                  title={image.label}
                  aria-label={image.label}
                  onClick={() => addSource(image.url, { name: image.label })}>
                  <img src={image.url} alt='' loading='lazy' />
                </button>
              ))}
            </div>
          </AssetsState>
        </PanelSection>
      ) : null}
    </PanelBody>
  );
}

export default UploadPanel;
